import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Tag from 'components/Tag';
import LoadingCircle from 'assets/icons/LoadingCircle';
import ButtonLoading from 'components/ButtonLoading';
import { useTranslation } from 'react-i18next';
import {
  STATUS_TAG_COLORS,
  STATUS,
  MAX_FILE_SIZE,
} from 'utils/constants/documents';
import moment from 'moment-timezone';
import { useDropzone } from 'react-dropzone';
import cx from 'classnames';
import {
  postDocumentUpload,
  postImageUpload,
  deleteImages,
} from 'utils/network/http';
import { readFile } from 'utils/functions';
import Tooltip from 'components/Tooltip';
import { DATE_WITH_MONTH_AND_YEAR } from 'utils/constants/formats';
import { sendEventToSegment } from 'utils/functions/Tracking';
import TRACKING_EVENTS from 'utils/constants/trackingEvents';
import config from 'utils/constants/config';
import useDocsCarrierProfile from 'hooks/useDocsCarrierProfile/useDocsCarrierProfile';
import { NOTIFICATION_TYPES } from 'utils/constants/notifications';
import Notification from 'components/Notification';
import { toast } from 'react-toastify';

const ACCEPTED_PDF = ['application/pdf'];
const ACCEPTED_IMAGES = ['image/jpg', 'image/jpeg'];

const errorList = {
  errorMoreThanOneFile: {
    title: 'errorMoreThanOneFile',
  },
  errorExceededFileSize: {
    title: 'errorExceededFileSizeTitle',
    content: 'errorExceededFileSizeContent',
  },
  errorUploadingDocuments: {
    title: 'errorUploadingDocuments',
  },
};

const DocumentsUpload = ({
  className,
  document,
  entity_type,
  uuid,
  refetchDocuments,
}) => {
  const { t } = useTranslation('Documents');
  const [uploading, setUploading] = useState(false);
  const { carrierProfile } = useDocsCarrierProfile();

  const displayErrorToast = useCallback(
    (error) => {
      toast(
        <Notification
          type={NOTIFICATION_TYPES.ERROR}
          title={t(error ? error.title : 'errorUploadingDocuments')}
          content={t(error ? error.content : '')}
        />,
        { autoClose: false, position: toast.POSITION.TOP_RIGHT }
      );
    },
    [t]
  );

  const uploadFile = useCallback(
    async (file, uploadFunction) => {
      try {
        setUploading(true);
        const readedFile = file ? await readFile(file) : '';
        await uploadFunction(
          uuid,
          entity_type,
          document.document_type.label,
          readedFile
        );
        await refetchDocuments();
        if (file) {
          sendEventToSegment(TRACKING_EVENTS.DMS_UPLOAD_DOCUMENT, {
            fileType: file?.type,
            documentType: document.document_type.label,
            entityType: entity_type,
          });
        }
      } catch (err) {
        console.error('uploadFiles', err);
        displayErrorToast(errorList.errorUploadingDocuments);
      } finally {
        setUploading(false);
      }
    },
    [uuid, entity_type, document, refetchDocuments, displayErrorToast]
  );

  const checkIfIsMoreThanOneFile = useCallback((files) => {
    return files.length === 1;
  }, []);

  const checkIfItsPDF = useCallback((files) => {
    return files[0].type === ACCEPTED_PDF[0];
  }, []);

  const checkFileSize = useCallback(
    (files) => files.every((file) => file.size <= MAX_FILE_SIZE),
    []
  );

  const checkAndSetErrors = useCallback(
    (files) => {
      if (!checkIfIsMoreThanOneFile(files)) {
        displayErrorToast(errorList.errorMoreThanOneFile);
        return false;
      } else if (!checkFileSize(files)) {
        displayErrorToast(errorList.errorExceededFileSize);
        return false;
      } else {
        return true;
      }
    },
    [checkIfIsMoreThanOneFile, checkFileSize, displayErrorToast]
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (checkAndSetErrors(acceptedFiles)) {
        uploadFile(
          acceptedFiles[0],
          checkIfItsPDF(acceptedFiles) || !document.two_sides
            ? postDocumentUpload
            : postImageUpload
        );
      }
    },
    [uploadFile, checkAndSetErrors, checkIfItsPDF, document]
  );

  const handleFinishProcess = useCallback(() => {
    uploadFile(null, postDocumentUpload);
  }, [uploadFile]);

  const handleCancelProcess = useCallback(() => {
    uploadFile(null, deleteImages);
    sendEventToSegment(TRACKING_EVENTS.DMS_CANCEL_DOCUMENT);
  }, [uploadFile]);

  const showUploadNextSide =
    document.status === STATUS.status_pending_next_face;

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: [...ACCEPTED_IMAGES, ...(showUploadNextSide ? [] : ACCEPTED_PDF)],
  });
  const dropzoneProps = getRootProps();
  const showUploadButton =
    document.status === STATUS.status_not_uploaded ||
    document.status === STATUS.status_close_to_expire ||
    document.status === STATUS.status_expired ||
    document.status === STATUS.status_invalid ||
    (document.status === STATUS.status_validated && document.renewable);

  const rejected = document.status === STATUS.status_invalid;

  const renderUploadLabel = useCallback(() => {
    if (document.status === STATUS.status_invalid) {
      return t('uploadAgain');
    } else if (
      document.status === STATUS.status_validated &&
      document.renewable
    ) {
      return t('renewDocument');
    } else return t('uploadDocument');
  }, [document, t]);

  const getDocumentSampleURL = useCallback(() => {
    const carrierCountry = carrierProfile?.carrier_info?.fiscal_country;
    return `${
      config.STATICS_URL
    }documentsSamples/${carrierCountry}/${document?.document_type?.label
      .toLowerCase()
      .split(' ')
      .join('_')}.png`;
  }, [carrierProfile, document]);

  return (
    <div className={className} data-testid="documents-upload">
      <div
        className="documents-upload"
        data-testid={`documents-upload-${entity_type}`}
      >
        <div
          {...getRootProps({
            className: `documents-upload__droparea`,
          })}
        >
          <input {...getInputProps()} data-testid="documents-upload__input" />
        </div>
        <div
          className={cx('documents-upload__item', {
            'documents-upload__item--next-face': showUploadNextSide,
          })}
        >
          {showUploadNextSide && (
            <div
              className="documents-upload__next-side"
              data-testid="documents-upload__next-side"
            >
              <span className="documents-upload__next-side-label">
                {t('twoSidesHelpText')}
              </span>
              {uploading && (
                <LoadingCircle className="documents-upload__spinner" />
              )}
              {!uploading && (
                <>
                  <div className="documents-upload__next-side-action">
                    <ButtonLoading
                      className="documents-upload__finish documents-upload__button documents-upload__button--auto"
                      text={t('finish')}
                      ghost
                      loading={uploading}
                      disabled={uploading}
                      handleClick={handleFinishProcess}
                      testId="documents-upload__finish-button"
                    />
                    <ButtonLoading
                      className="documents-upload__upload-other documents-upload__button documents-upload__button--auto"
                      text={t('uploadNextSide')}
                      handleClick={dropzoneProps.onClick}
                      skin="secondary"
                      loading={uploading}
                      disabled={uploading}
                      testId="documents-upload__next-side-button"
                    />
                  </div>
                  <ButtonLoading
                    icon="close-thin"
                    nude
                    className="documents-upload__cancel-icon"
                    handleClick={handleCancelProcess}
                    loading={uploading}
                    disabled={uploading}
                    testId="documents-upload__cancel-button"
                  />
                </>
              )}
            </div>
          )}
          {!showUploadNextSide && (
            <>
              <div className="documents-upload__tag-container">
                <Tag
                  className="documents-upload__tag"
                  text={t(document.status)}
                  skin={STATUS_TAG_COLORS[document.status] || 'grey'}
                ></Tag>
                {rejected && document.comment && (
                  <Tooltip
                    buttonText={t('knowMore')}
                    buttonClass="documents-upload__link-text-error"
                    skin={STATUS_TAG_COLORS[STATUS.status_invalid]}
                  >
                    <span className="documents-upload__tooltip-error">
                      {t(document.comment)}
                    </span>
                  </Tooltip>
                )}
              </div>
              <div
                className={cx('documents-upload__doc-title', {
                  'documents-upload__doc-title--push-side': !showUploadButton,
                })}
              >
                {t(`documentType${document.document_type.id}`)}
              </div>
              {showUploadButton && (
                <div className="documents-upload__link">
                  <Tooltip
                    buttonText={t('seeSample')}
                    buttonClass="documents-upload__link-text"
                  >
                    <div className="documents-upload__example-modal">
                      <img
                        className="documents-upload__example-image"
                        src={getDocumentSampleURL()}
                        alt={document.document_type.label}
                      />
                    </div>
                  </Tooltip>
                </div>
              )}
              {document.expiration_date && (
                <div className="documents-upload__expire-value documents-upload__expire-value--alone">
                  {moment(document.expiration_date).format(
                    DATE_WITH_MONTH_AND_YEAR
                  )}
                </div>
              )}
              {document.document_type.group &&
                document.document_type.group === 'optional' && (
                  <div className="documents-upload__optional-tag">
                    ({t('noRequired')})
                  </div>
                )}
              <div className="documents-upload__actions">
                <div className="documents-upload__expire-container">
                  {document.expiration_date && (
                    <>
                      <span className="documents-upload__expire-label">
                        {`${t('expire')}:`}
                      </span>
                      <span className="documents-upload__expire-value">
                        {moment(document.expiration_date).format(
                          DATE_WITH_MONTH_AND_YEAR
                        )}
                      </span>
                    </>
                  )}
                </div>
                <div className="documents-upload__button">
                  {showUploadButton && (
                    <ButtonLoading
                      data-testid="documents-upload__button"
                      loading={uploading}
                      disabled={uploading}
                      iconPosition="left"
                      ghost={true}
                      text={renderUploadLabel()}
                      handleClick={dropzoneProps.onClick}
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
DocumentsUpload.propTypes = {
  className: PropTypes.string,
  document: PropTypes.object.isRequired,
  entity_type: PropTypes.string.isRequired,
  uuid: PropTypes.string.isRequired,
  refetchDocuments: PropTypes.func.isRequired,
};
export default DocumentsUpload;
